import { FormControlLabel, Paper, Stack, Switch, Typography } from "@mui/material";
import { useState } from "react";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import InvestorCommunicationContext from "../InvestorCommunicationContext";
import { isContentStepCompleted } from "../investorCommunicationEditState";
import type { VideoEditState } from "../investorCommunicationVideoEditState";
import { setAllowDownloadAction } from "../investorCommunicationVideoEditState";
import InvestorCommunicationContentFormFields from "./InvestorCommunicationContentFormFields";
import InvestorCommunicationContentFormHeader from "./InvestorCommunicationContentFormHeader";
import InvestorCommunicationContentPreviewDialog from "./InvestorCommunicationContentPreviewDialog";
import InvestorCommunicationImageField from "./InvestorCommunicationImageField";
import InvestorCommunicationVideoContentPreview from "./InvestorCommunicationVideoContentPreview";
import InvestorCommunicationVideoFileField from "./InvestorCommunicationVideoFileField";

const thumbnailProgressText = "Creating a thumbnail from the video…";

const thumbnailHint =
  "Optional. If you don't add one, a frame from the video is used. Recommended 1200 × 800 px (3:2). PNG, JPG or WEBP, up to 25 MB.";

interface Props {
  videoState: VideoEditState;
}

const InvestorCommunicationVideoContentForm = ({ videoState }: Props) => {
  const {
    state,
    setState,
    isSaving,
    draftVideoUrl,
    draftThumbnailSource,
    thumbnailCaptureError,
    isGeneratingThumbnail,
    onThumbnailFileAdd,
    onThumbnailFileRemove,
  } = useDefinedContext(InvestorCommunicationContext, "InvestorCommunicationContext");

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  return (
    <Stack spacing={2.5}>
      <InvestorCommunicationContentFormHeader
        title="Video content"
        previewDisabled={isSaving || !isContentStepCompleted(state)}
        onPreviewClick={() => setIsPreviewOpen(true)}
      />

      <InvestorCommunicationContentFormFields />

      <InvestorCommunicationVideoFileField videoState={videoState} />

      <InvestorCommunicationImageField
        title="Thumbnail"
        hint={thumbnailHint}
        type={state.savedCommunication.type}
        source={draftThumbnailSource}
        error={thumbnailCaptureError}
        disabled={isSaving}
        progressText={isGeneratingThumbnail ? thumbnailProgressText : undefined}
        onFileAdd={onThumbnailFileAdd}
        onRemove={onThumbnailFileRemove}
      />

      <Paper variant="outlined" sx={{ px: 2, py: 1.5 }}>
        <FormControlLabel
          labelPlacement="start"
          disabled={isSaving}
          control={
            <Switch
              checked={videoState.allowDownload}
              onChange={(e) => setState(setAllowDownloadAction(e.target.checked))}
            />
          }
          label={
            <Stack spacing={0.5}>
              <Typography variant="subtitle1">Allow download</Typography>
              <Typography color="textSecondary">Let investors download the original video file.</Typography>
            </Stack>
          }
          sx={{ display: "flex", justifyContent: "space-between", width: "100%", m: 0 }}
        />
      </Paper>

      {isPreviewOpen && (
        <InvestorCommunicationContentPreviewDialog onClose={() => setIsPreviewOpen(false)}>
          <InvestorCommunicationVideoContentPreview
            title={state.title}
            categoryIds={state.categoryIds}
            description={state.description}
            videoUrl={draftVideoUrl}
            thumbnailSource={draftThumbnailSource}
            allowDownload={videoState.allowDownload}
          />
        </InvestorCommunicationContentPreviewDialog>
      )}
    </Stack>
  );
};

export default InvestorCommunicationVideoContentForm;
